import React, { useState, useEffect } from 'react';
import { ArrowRight, Plus, Edit2, Trash2, X } from 'lucide-react';

const API_URL = 'https://metapi.emedhatechnologies.com/api/courses/api/announcements';

// Category Style Config Matching Design
const CATEGORY_STYLES = {
  Admission: { dot: '#ef4444', bg: '#e0e7ff', color: '#1d4ed8' },
  Exam: { dot: '#ef4444', bg: '#dcfce7', color: '#15803d' },
  Assignment: { dot: '#ef4444', bg: '#ffe4e6', color: '#be123c' },
  Holiday: { dot: '#ef4444', bg: '#fef3c7', color: '#b45309' },
  Event: { dot: '#ef4444', bg: '#f3e8ff', color: '#7e22ce' },
  Default: { dot: '#ef4444', bg: '#f1f5f9', color: '#475569' },
};

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form State (Added header_title)
  const [formData, setFormData] = useState({
    header_title: 'LATEST UPDATES',
    title: '',
    category: 'Admission',
    date_posted: new Date().toISOString().split('T')[0],
    is_new: false,
  });

  // Fetch Announcements
  const fetchAnnouncements = async () => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      setItems(data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Handle Form Input Changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Open Modal (for Create or Edit)
  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        header_title: item.header_title || 'LATEST UPDATES',
        title: item.title,
        category: item.category,
        date_posted: item.date_posted ? item.date_posted.split('T')[0] : '',
        is_new: Boolean(item.is_new),
      });
    } else {
      setEditingItem(null);
      setFormData({
        header_title: 'LATEST UPDATES',
        title: '',
        category: 'Admission',
        date_posted: new Date().toISOString().split('T')[0],
        is_new: false,
      });
    }
    setModalOpen(true);
  };

  // Submit Handler (POST or PUT)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = editingItem ? 'PUT' : 'POST';
    const url = editingItem ? `${API_URL}/${editingItem.id}` : API_URL;

    try {
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setModalOpen(false);
      fetchAnnouncements();
    } catch (err) {
      console.error('Error saving item:', err);
    }
  };

  // Delete Handler
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        await fetch(`${API_URL}/${id}`, { method: 'DELETE' });
        fetchAnnouncements();
      } catch (err) {
        console.error('Error deleting item:', err);
      }
    }
  };

  // Format Date String
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={{ maxWidth: '980px', margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* SECTION HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' }}>
        <div>
          <div style={{ color: '#1e3a8a', fontSize: '12px', fontWeight: '800', letterSpacing: '0.05em', marginBottom: '4px' }}>
            LATEST UPDATES
          </div>
          <h2 style={{ color: '#0f172a', fontSize: '28px', fontWeight: '800', margin: 0 }}>
            Announcements & News
          </h2>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => openModal()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#1d4ed8',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Add Post
          </button>
        </div>
      </div>

      {/* ANNOUNCEMENT LIST */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {items.map((item) => {
          const badgeStyle = CATEGORY_STYLES[item.category] || CATEGORY_STYLES.Default;

          return (
            <div
              key={item.id}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '18px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                transition: 'border-color 0.2s',
              }}
            >
              <div style={{ flex: 1 }}>
                {/* Meta Row: Dot, Category, Date, NEW Tag */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: badgeStyle.dot }} />
                  
                  <span
                    style={{
                      backgroundColor: badgeStyle.bg,
                      color: badgeStyle.color,
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '3px 10px',
                      borderRadius: '12px',
                    }}
                  >
                    {item.category}
                  </span>

                  <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '500' }}>
                    {formatDate(item.date_posted)}
                  </span>

                  {item.is_new ? (
                    <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: '800', letterSpacing: '0.02em' }}>
                      NEW
                    </span>
                  ) : null}
                </div>

                {/* Announcement Header Title & Main Title */}
                {item.header_title && (
                  <div style={{ fontSize: '11px', fontWeight: '800', color: '#1e3a8a', letterSpacing: '0.04em', marginBottom: '2px' }}>
                    {item.header_title}
                  </div>
                )}
                <h3 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0, lineHeight: 1.4 }}>
                  {item.title}
                </h3>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={() => openModal(item)}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: '#475569' }}
                  title="Edit"
                >
                  <Edit2 size={15} />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  style={{ background: '#ffe4e6', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: '#be123c' }}
                  title="Delete"
                >
                  <Trash2 size={15} />
                </button>
                <ArrowRight size={18} color="#cbd5e1" style={{ marginLeft: '4px' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* CREATE/EDIT MODAL WITH HEADER TITLE FIELD */}
      {modalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px', padding: '24px', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                {editingItem ? 'Edit Announcement' : 'Add Announcement'}
              </h3>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} color="#64748b" />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Header Title Input */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Header Title</label>
                <input
                  type="text"
                  name="header_title"
                  value={formData.header_title}
                  onChange={handleChange}
                  required
                  placeholder="e.g. LATEST UPDATES"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Title Input */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Description</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="Enter main Description"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              {/* Category & Date Row */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff' }}
                  >
                    <option value="Admission">Admission</option>
                    <option value="Exam">Exam</option>
                    <option value="Assignment">Assignment</option>
                    <option value="Holiday">Holiday</option>
                    <option value="Event">Event</option>
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Date</label>
                  <input
                    type="date"
                    name="date_posted"
                    value={formData.date_posted}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Is New Checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <input
                  type="checkbox"
                  id="is_new"
                  name="is_new"
                  checked={formData.is_new}
                  onChange={handleChange}
                  style={{ width: '16px', height: '16px' }}
                />
                <label htmlFor="is_new" style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>
                  Mark as "NEW" badge
                </label>
              </div>

              <button
                type="submit"
                style={{
                  marginTop: '12px',
                  backgroundColor: '#1d4ed8',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {editingItem ? 'Update Announcement' : 'Create Announcement'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}